import React, { useState } from 'react';
import {
  Check,
  Circle
} from '@phosphor-icons/react';

const OpportunityStatusPipeline = ({ status, onStatusChange }) => {
  const [updating, setUpdating] = useState(null);

  const STATUS_STEPS = [
    { value: 'identified', label: 'Identified' },
    { value: 'evaluating', label: 'Evaluating' },
    { value: 'planning', label: 'Planning' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'implemented', label: 'Implemented' }
  ];

  const currentIndex = STATUS_STEPS.findIndex(step => step.value === status);

  const handleStatusClick = async (newStatus, index) => {
    if (updating || status === newStatus) return;
    
    setUpdating(index);
    
    try {
      await onStatusChange(newStatus);
      // Status update successful
      setTimeout(() => {
        setUpdating(null);
      }, 400);
    } catch (error) {
      console.error('Failed to update status:', error);
      setUpdating(null);
    }
  };

  const getStepState = (index) => {
    if (index < currentIndex) return 'completed';
    if (index === currentIndex) return 'current';
    return 'future';
  };

  const getStepStyles = (state, index) => {
    const isUpdating = updating === index;
    
    if (state === 'completed') {
      return {
        circle: 'bg-gradient-to-br from-[#2ECC71] to-[#27AE60] border-2 border-[#2ECC71] shadow-lg shadow-[#2ECC71]/30',
        label: 'text-[#2ECC71] font-semibold',
        icon: 'text-white',
        connector: 'bg-gradient-to-r from-[#2ECC71] to-[#27AE60]'
      };
    }
    
    if (state === 'current') {
      return {
        circle: `bg-gradient-to-br from-[#7B2FF7] to-[#F107A3] border-2 border-[#7B2FF7] shadow-xl shadow-[#7B2FF7]/50 ${isUpdating ? 'animate-pulse' : 'hover:scale-110'}`,
        label: 'text-white font-bold',
        icon: 'text-white',
        connector: 'bg-[rgba(255,255,255,0.2)]'
      };
    }
    
    return {
      circle: `border-2 border-[#4A4A4A] bg-[rgba(255,255,255,0.03)] ${isUpdating ? 'animate-pulse' : 'hover:scale-105 hover:border-[#6A6A6A]'}`,
      label: 'text-[#A0A0A0]',
      icon: 'text-[#6A6A6A]',
      connector: 'bg-[rgba(255,255,255,0.1)]'
    };
  };

  return (
    <div className="w-full">
      {/* Desktop View - Horizontal Pipeline */}
      <div className="hidden md:flex items-center justify-between px-4">
        {STATUS_STEPS.map((step, index) => {
          const state = getStepState(index);
          const styles = getStepStyles(state, index);
          const isLast = index === STATUS_STEPS.length - 1;

          return (
            <React.Fragment key={step.value}>
              {/* Step */}
              <div className="flex flex-col items-center gap-3 flex-shrink-0">
                {/* Circle */}
                <button
                  onClick={() => handleStatusClick(step.value, index)}
                  disabled={updating !== null}
                  className={`
                    relative w-8 h-8 rounded-full flex items-center justify-center
                    transition-all duration-300 ease-in-out cursor-pointer
                    ${styles.circle}
                    ${updating !== null && updating !== index ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  title={`Update to ${step.label}`}
                >
                  {state === 'completed' ? (
                    <Check size={18} className={styles.icon} strokeWidth={3} />
                  ) : state === 'current' ? (
                    <div className="w-3 h-3 rounded-full bg-white animate-pulse" />
                  ) : (
                    <Circle size={14} className={styles.icon} />
                  )}
                </button>

                {/* Label */}
                <span className={`text-xs font-medium transition-colors ${styles.label} whitespace-nowrap`}>
                  {step.label}
                </span>
              </div>

              {/* Connector Line */}
              {!isLast && (
                <div className="flex-1 mx-2 h-0.5 relative">
                  <div className={`absolute inset-0 ${styles.connector} transition-all duration-500`} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Mobile View - Horizontal Scroll */}
      <div className="md:hidden overflow-x-auto scrollbar-hide">
        <div className="flex items-center gap-4 px-4 pb-2 min-w-max">
          {STATUS_STEPS.map((step, index) => {
            const state = getStepState(index);
            const styles = getStepStyles(state, index);
            const isLast = index === STATUS_STEPS.length - 1;

            return (
              <React.Fragment key={step.value}>
                {/* Step */}
                <div className="flex flex-col items-center gap-2 flex-shrink-0">
                  {/* Circle */}
                  <button
                    onClick={() => handleStatusClick(step.value, index)}
                    disabled={updating !== null}
                    className={`
                      relative w-10 h-10 rounded-full flex items-center justify-center
                      transition-all duration-300 ease-in-out
                      ${styles.circle}
                      ${updating !== null && updating !== index ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                    title={`Update to ${step.label}`}
                  >
                    {state === 'completed' ? (
                      <Check size={20} className={styles.icon} strokeWidth={3} />
                    ) : state === 'current' ? (
                      <div className="w-3 h-3 rounded-full bg-white animate-pulse" />
                    ) : (
                      <Circle size={16} className={styles.icon} />
                    )}
                  </button>

                  {/* Label */}
                  <span className={`text-xs font-medium ${styles.label} whitespace-nowrap`}>
                    {step.label}
                  </span>
                </div>

                {/* Connector Line */}
                {!isLast && (
                  <div className="w-8 h-0.5 relative flex-shrink-0">
                    <div className={`absolute inset-0 ${styles.connector} transition-all duration-500`} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OpportunityStatusPipeline;
